// Liver Health Assessment — question set (display). Backend owns scoring.
// Each question / help / option has optional `_hi` and `_gu` fields for
// bilingual display. English is always primary. Local language is optional.

export const QUESTIONS = [
  {
    id: "age",
    category: "Profile",
    type: "age",
    question: "What is your age?",
    question_hi: "आपकी उम्र क्या है?",
    question_gu: "તમારી ઉંમર કેટલી છે?",
    help: "Enter your age in years — we'll do the rest.",
    help_hi: "अपनी उम्र वर्षों में दर्ज करें — बाकी हम कर लेंगे।",
    help_gu: "તમારી ઉંમર વર્ષોમાં દાખલ કરો — બાકી અમે કરી લઈશું।",
    min: 1,
    max: 120,
    options: [
      { id: "a", label: "Less than 35 years", label_hi: "35 वर्ष से कम", label_gu: "35 વર્ષથી ઓછી" },
      { id: "b", label: "35–44 years", label_hi: "35–44 वर्ष", label_gu: "35–44 વર્ષ" },
      { id: "c", label: "45–54 years", label_hi: "45–54 वर्ष", label_gu: "45–54 વર્ષ" },
      { id: "d", label: "55 years or older", label_hi: "55 वर्ष या अधिक", label_gu: "55 વર્ષ કે વધુ" },
    ],
  },
  {
    id: "waist",
    category: "Body Composition",
    question: "What is your waist circumference?",
    question_hi: "आपकी कमर की माप क्या है?",
    question_gu: "તમારી કમરનું માપ કેટલું છે?",
    help: "Measure around the abdomen at the level of the belly button.",
    help_hi: "नाभि के स्तर पर पेट के चारों ओर नापें।",
    help_gu: "નાભિની ઊંચાઈ પર પેટ ફરતે માપો।",
    optionsByGender: {
      male: [
        { id: "a", label: "Less than 90 cm (<35.5 in)" },
        { id: "b", label: "90–99 cm (35.5–39 in)" },
        { id: "c", label: "≥100 cm (≥39.4 in)" },
      ],
      female: [
        { id: "a", label: "Less than 80 cm (<31.5 in)" },
        { id: "b", label: "80–89 cm (31.5–35 in)" },
        { id: "c", label: "≥90 cm (≥35.4 in)" },
      ],
    },
    options: [
      { id: "a", label: "Less than 90 cm (<35.5 in)" },
      { id: "b", label: "90–99 cm (35.5–39 in)" },
      { id: "c", label: "≥100 cm (≥39.4 in)" },
    ],
  },
  {
    id: "bmi",
    category: "Body Composition",
    type: "bmi",
    question: "What is your height and weight?",
    question_hi: "आपकी लंबाई और वजन कितना है?",
    question_gu: "તમારી ઊંચાઈ અને વજન કેટલું છે?",
    help: "We'll calculate your BMI automatically — no need to work it out.",
    help_hi: "हम आपका BMI अपने आप निकाल लेंगे।",
    help_gu: "અમે તમારો BMI આપોઆપ ગણી લઈશું।",
    options: [
      { id: "a", label: "BMI less than 23", label_hi: "BMI 23 से कम", label_gu: "BMI 23 થી ઓછું" },
      { id: "b", label: "BMI 23–24.9", label_hi: "BMI 23–24.9", label_gu: "BMI 23–24.9" },
      { id: "c", label: "BMI 25 or above", label_hi: "BMI 25 या अधिक", label_gu: "BMI 25 કે વધુ" },
    ],
  },
  {
    id: "diabetes",
    category: "Metabolic",
    question: "Have you ever been told you have prediabetes or Type 2 diabetes?",
    question_hi: "क्या आपको कभी प्री-डायबिटीज या टाइप 2 डायबिटीज बताया गया है?",
    question_gu: "શું તમને ક્યારેય પ્રી-ડાયાબિટીસ કે ટાઈપ 2 ડાયાબિટીસ કહેવામાં આવ્યું છે?",
    help: "Insulin resistance is one of the strongest drivers of fatty liver.",
    help_hi: "इंसुलिन रेजिस्टेंस फैटी लीवर का बड़ा कारण है।",
    help_gu: "ઈન્સ્યુલિન રેઝિસ્ટન્સ ફેટી લિવરનું મુખ્ય કારણ છે।",
    reference: {
      source: "ADA 2025",
      cols: ["Fasting glucose", "HbA1c"],
      rows: [
        { label: "Normal",      values: ["< 100 mg/dL",   "< 5.7%"],   color: "#22C55E" },
        { label: "Prediabetes", values: ["100–125 mg/dL", "5.7–6.4%"], color: "#EAB308" },
        { label: "Diabetes",    values: ["≥ 126 mg/dL",   "≥ 6.5%"],   color: "#EF4444" },
      ],
    },
    options: [
      { id: "a", label: "No", label_hi: "नहीं", label_gu: "ના" },
      { id: "b", label: "Prediabetes", label_hi: "प्री-डायबिटीज", label_gu: "પ્રી-ડાયાબિટીસ" },
      { id: "c", label: "Type 2 diabetes", label_hi: "टाइप 2 डायबिटीज", label_gu: "ટાઈપ 2 ડાયાબિટીસ" },
    ],
  },
  {
    id: "hypertension",
    category: "Metabolic",
    question: "Do you have high blood pressure or take medication for it?",
    question_hi: "क्या आपको हाई ब्लड प्रेशर है या उसकी दवा लेते हैं?",
    question_gu: "શું તમને હાઈ બ્લડ પ્રેશર છે અથવા દવા લો છો?",
    help: "Hypertension is closely linked with metabolic liver disease.",
    help_hi: "हाई बीपी लीवर की मेटाबॉलिक बीमारी से जुड़ा है।",
    help_gu: "હાઈ બીપી લિવરની મેટાબોલિક બીમારી સાથે જોડાયેલું છે।",
    reference: {
      source: "AHA / ACC 2024",
      cols: ["Systolic (mmHg)", "Diastolic (mmHg)"],
      rows: [
        { label: "Normal",      values: ["< 120",   "< 80"],  color: "#22C55E" },
        { label: "Elevated",    values: ["120–129", "< 80"],  color: "#84CC16" },
        { label: "Stage 1 HTN", values: ["130–139", "80–89"], color: "#EAB308" },
        { label: "Stage 2 HTN", values: ["≥ 140",   "≥ 90"],  color: "#EF4444" },
      ],
    },
    options: [
      { id: "a", label: "No", label_hi: "नहीं", label_gu: "ના" },
      { id: "b", label: "Yes", label_hi: "हाँ", label_gu: "હા" },
    ],
  },
  {
    id: "cholesterol",
    category: "Metabolic",
    question: "Have you been told you have high cholesterol or high triglycerides, or do you take medication for them?",
    question_hi: "क्या आपको हाई कोलेस्ट्रॉल या हाई ट्राइग्लिसराइड्स बताया गया है, या इसकी दवा लेते हैं?",
    question_gu: "શું તમને હાઈ કોલેસ્ટ્રોલ કે હાઈ ટ્રાઈગ્લિસરાઈડ્સ કહેવામાં આવ્યું છે, કે તેની દવા લો છો?",
    help: "Elevated blood lipids and liver fat travel together.",
    help_hi: "बढ़े हुए लिपिड और लीवर की चर्बी साथ-साथ चलते हैं।",
    help_gu: "વધેલા લિપિડ અને લિવરની ચરબી સાથે-સાથે ચાલે છે।",
    reference: {
      source: "AHA / ACC / NLA 2024 — units: mg/dL",
      cols: ["Total", "LDL", "HDL", "Triglycerides"],
      rows: [
        { label: "Optimal",    values: ["< 200",   "< 100",   "≥ 60",         "< 150"], color: "#22C55E" },
        { label: "Borderline", values: ["200–239", "130–159", "40–59 M / 50–59 W", "150–199"], color: "#EAB308" },
        { label: "High",       values: ["≥ 240",   "160–189", "< 40 M / < 50 W",   "200–499"], color: "#F97316" },
        { label: "Very high",  values: ["—",       "≥ 190",   "—",            "≥ 500"], color: "#EF4444" },
      ],
      footnote: "M = men · W = women. HDL is protective — higher is better.",
    },
    options: [
      { id: "a", label: "No", label_hi: "नहीं", label_gu: "ના" },
      { id: "b", label: "Yes", label_hi: "हाँ", label_gu: "હા" },
    ],
  },
  {
    id: "alcohol",
    category: "Lifestyle",
    question: "How much alcohol do you usually drink?",
    question_hi: "आप आमतौर पर कितनी शराब पीते हैं?",
    question_gu: "તમે સામાન્ય રીતે કેટલી શરાબ પીઓ છો?",
    help: "Alcohol is processed almost entirely by the liver.",
    help_hi: "शराब लगभग पूरी तरह लीवर में पचती है।",
    help_gu: "શરાબ લગભગ સંપૂર્ણપણે લિવરમાં પચે છે।",
    options: [
      { id: "a", label: "Never or rarely", label_hi: "कभी नहीं या बहुत कम", label_gu: "ક્યારેય નહીં કે બહુ ઓછું" },
      { id: "b", label: "1–7 drinks / week", label_hi: "1–7 ड्रिंक्स / सप्ताह", label_gu: "1–7 ડ્રિંક્સ / અઠવાડિયું" },
      { id: "c", label: "8–14 drinks / week", label_hi: "8–14 ड्रिंक्स / सप्ताह", label_gu: "8–14 ડ્રિંક્સ / અઠવાડિયું" },
      { id: "d", label: "More than 14 / week or regular binge drinking", label_hi: "14 से अधिक / सप्ताह या बार-बार बहुत पीना", label_gu: "14 થી વધુ / અઠવાડિયું કે વારંવાર વધુ પીવું" },
    ],
    note: {
      title: "One standard drink",
      title_hi: "एक स्टैंडर्ड ड्रिंक",
      title_gu: "એક સ્ટાન્ડર્ડ ડ્રિંક",
      body: "Approximately 330 mL beer, 150 mL wine, or 30 mL spirits.",
      body_hi: "लगभग 330 मिली बीयर, 150 मिली वाइन, या 30 मिली स्पिरिट।",
      body_gu: "આશરે 330 મિ.લી. બિયર, 150 મિ.લી. વાઈન કે 30 મિ.લી. સ્પિરિટ।",
      caption:
        "Sex-specific alcohol exposure and drinking pattern should additionally be considered during clinical assessment.",
      caption_hi: "क्लिनिकल मूल्यांकन में लिंग के अनुसार शराब के सेवन और पैटर्न पर भी विचार किया जाना चाहिए।",
      caption_gu: "તબીબી મૂલ્યાંકનમાં લિંગ પ્રમાણે શરાબનું સેવન અને પેટર્ન ધ્યાનમાં લેવું જોઈએ।",
      visuals: [
        { icon: "beer", label: "330 mL beer", label_hi: "330 मिली बीयर", label_gu: "330 મિ.લી. બિયર" },
        { icon: "wine", label: "150 mL wine", label_hi: "150 मिली वाइन", label_gu: "150 મિ.લી. વાઈન" },
        { icon: "spirit", label: "30 mL spirits", label_hi: "30 मिली स्पिरिट", label_gu: "30 મિ.લી. સ્પિરિટ" },
      ],
    },
  },
  {
    id: "activity",
    category: "Lifestyle",
    question: "Do you exercise for at least 150 minutes per week?",
    question_hi: "क्या आप हफ्ते में कम से कम 150 मिनट व्यायाम करते हैं?",
    question_gu: "શું તમે અઠવાડિયામાં ઓછામાં ઓછું 150 મિનિટ કસરત કરો છો?",
    help: "Movement clears fat directly from the liver.",
    help_hi: "व्यायाम से लीवर की चर्बी कम होती है।",
    help_gu: "કસરતથી લિવરની ચરબી ઘટે છે।",
    options: [
      { id: "a", label: "Yes", label_hi: "हाँ", label_gu: "હા" },
      { id: "b", label: "No", label_hi: "नहीं", label_gu: "ના" },
    ],
  },
  {
    id: "sleep",
    category: "Lifestyle",
    question: "Do you snore loudly or have diagnosed sleep apnea?",
    question_hi: "क्या आप ज़ोर से खर्राटे लेते हैं या स्लीप एप्निया है?",
    question_gu: "શું તમે જોરથી નસકોરાં લો છો કે સ્લીપ એપ્નિયા છે?",
    help: "Poor sleep and low oxygen at night stress the liver.",
    help_hi: "खराब नींद और रात में कम ऑक्सीजन लीवर पर दबाव डालते हैं।",
    help_gu: "ખરાબ ઊંઘ અને રાત્રે ઓછું ઓક્સિજન લિવર પર દબાણ કરે છે।",
    options: [
      { id: "a", label: "No", label_hi: "नहीं", label_gu: "ના" },
      { id: "b", label: "Yes", label_hi: "हाँ", label_gu: "હા" },
    ],
  },
  {
    id: "diagnosis",
    category: "History",
    question: "Has a doctor or scan ever told you that you have fatty liver?",
    question_hi: "क्या डॉक्टर या स्कैन ने आपको फैटी लीवर बताया है?",
    question_gu: "શું ડોક્ટર કે સ્કેને તમને ફેટી લિવર કહ્યું છે?",
    help: "A prior finding changes your baseline liver risk.",
    help_hi: "पहले का निदान बेसलाइन जोखिम बदल देता है।",
    help_gu: "અગાઉનું નિદાન બેઝલાઈન જોખમ બદલે છે।",
    options: [
      { id: "a", label: "No", label_hi: "नहीं", label_gu: "ના" },
      { id: "b", label: "Yes", label_hi: "हाँ", label_gu: "હા" },
    ],
  },
  {
    id: "family",
    category: "History",
    question: "Any family history of liver disease?",
    question_hi: "क्या परिवार में किसी को लीवर की बीमारी है/थी?",
    question_gu: "શું પરિવારમાં કોઈને લિવરની બીમારી છે/હતી?",
    help: "Genetics can raise your baseline risk.",
    help_hi: "पारिवारिक इतिहास बेसलाइन जोखिम बढ़ा सकता है।",
    help_gu: "પારિવારિક ઇતિહાસ બેઝલાઈન જોખમ વધારી શકે છે।",
    options: [
      { id: "a", label: "No", label_hi: "नहीं", label_gu: "ના" },
      { id: "b", label: "Yes", label_hi: "हाँ", label_gu: "હા" },
    ],
  },
  {
    id: "redflags",
    category: "Medical Red Flags",
    type: "multiselect",
    question: "Do you currently have any of the following?",
    question_hi: "क्या इनमें से कोई लक्षण अभी है?",
    question_gu: "શું આમાંથી કોઈ લક્ષણ અત્યારે છે?",
    help: "These are not part of your 100-point score — but any yes triggers an immediate clinical recommendation.",
    help_hi: "ये 100-अंक स्कोर का हिस्सा नहीं हैं — पर कोई भी 'हाँ' तुरंत डॉक्टर की सलाह की माँग करता है।",
    help_gu: "આ 100-પોઈન્ટ સ્કોરનો ભાગ નથી — પણ કોઈપણ 'હા' એટલે તાત્કાલિક ડોક્ટરની સલાહ।",
    options: [
      { id: "none", label: "No — none of these apply", label_hi: "नहीं — इनमें से कोई नहीं", label_gu: "ના — આમાંથી કોઈ નથી", exclusive: true },
      { id: "liver_disease", label: "Known case of liver disease", label_hi: "लीवर की बीमारी है", label_gu: "લિવરની બીમારી છે" },
      { id: "cirrhosis", label: "Known case of liver fibrosis / cirrhosis", label_hi: "लीवर फाइब्रोसिस / सिरोसिस है", label_gu: "લિવર ફાઈબ્રોસિસ / સિરોસિસ છે" },
      { id: "jaundice", label: "Yellow eyes/skin or new jaundice", label_hi: "पीली आंखें/त्वचा या नया पीलिया", label_gu: "પીળી આંખો/ત્વચા કે નવો કમળો", urgent: true },
      { id: "swelling", label: "New or increasing abdominal swelling", label_hi: "पेट में नई या बढ़ती सूजन", label_gu: "પેટમાં નવો કે વધતો સોજો", urgent: true },
      { id: "vomit_blood", label: "Vomiting blood", label_hi: "खून की उल्टी", label_gu: "લોહી ઊલ્ટી", emergency: true },
      { id: "black_stool", label: "Black / tarry stools", label_hi: "काला / गोंद जैसा मल", label_gu: "કાળા / ડામર જેવો મળ", emergency: true },
      { id: "confusion", label: "New confusion or unusual excessive drowsiness", label_hi: "नया भ्रम या असामान्य तेज़ नींद", label_gu: "નવો ગૂંચવણ કે અસામાન્ય વધુ ઊંઘ", emergency: true },
    ],
    note: {
      title: "If yes",
      title_hi: "यदि हाँ",
      title_gu: "જો હા",
      body: "Medical assessment is required promptly. Vomiting blood, black stools, significant confusion or other severe / rapidly worsening symptoms may require urgent or emergency medical evaluation.",
      body_hi: "तुरंत चिकित्सा जाँच आवश्यक है। खून की उल्टी, काला मल, गंभीर भ्रम या तेज़ी से बिगड़ते लक्षण आपातकालीन उपचार माँग सकते हैं।",
      body_gu: "તાત્કાલિક તબીબી તપાસ જરૂરી છે। લોહી ઊલ્ટી, કાળો મળ, ગંભીર ગૂંચવણ કે ઝડપથી બગડતાં લક્ષણો કટોકટીની સારવાર માંગી શકે છે।",
    },
  },
];

export const RISK_BANDS = {
  green: { label: "Low Risk", hex: "#22C55E", ring: "#16A34A" },
  yellow: { label: "Moderate Risk", hex: "#EAB308", ring: "#CA8A04" },
  orange: { label: "High Risk", hex: "#F97316", ring: "#EA580C" },
  red: { label: "Very High Risk", hex: "#EF4444", ring: "#DC2626" },
};
